import { useAppTranslations } from '@treneo/shared/i18n';
import { IconColors, IconSizes, InfoIcon } from '@treneo/shared/icons';
import { Colors } from '@treneo/shared/themes';
import { createTimezoneMismatchMessage, isTimezoneMismatch } from '@treneo/shared/utils';

import { IconButton } from './buttons/icon-button';
import { Alert } from './components/alerts';
import { Tooltip } from './components/tooltip';
import { styled } from './styled';

const StyledAlert = styled(Alert)`
  margin-bottom: 8px;

  .MuiAlert-message {
    font-size: 0.875rem;
  }
`;

const InlineWarning = styled('span')`
  color: ${Colors.warning};
  font-size: 0.75rem;
  margin-left: 4px;
`;

type Props = {
  userTimezone?: string;
  variant?: 'alert' | 'inline' | 'tooltip';
  className?: string;
};

export const TimezoneWarning = ({
  userTimezone,
  variant = 'alert',
  className
}: Readonly<Props>) => {
  const { t } = useAppTranslations();

  if (!userTimezone || !isTimezoneMismatch(userTimezone)) {
    return null;
  }

  const message = createTimezoneMismatchMessage(userTimezone);

  switch (variant) {
    case 'inline':
      return <InlineWarning className={className}>{t('differentTimezone')}</InlineWarning>;

    case 'tooltip':
      return (
        <Tooltip title={message} arrow>
          <IconButton size='small' className={className}>
            <InfoIcon fontSize={IconSizes.small} color={IconColors.warning} />
          </IconButton>
        </Tooltip>
      );

    case 'alert':
    default:
      return (
        <StyledAlert severity='info' variant='outlined' className={className}>
          {message}
        </StyledAlert>
      );
  }
};
